import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ArrowLeft, Users, User, RefreshCw, Info } from 'lucide-react';
import { useMessage } from '@/hooks/use-message';
import { useAuth } from '@/hooks/use-auth';
import { MessageInput } from './MessageInput';
import { format } from 'date-fns';
import { useLanguage } from '@/hooks/use-language';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { ContactInfoSheet } from './ContactInfoSheet';
import { devLog } from '@/utils/console-utils';

const DEBUG = true;

interface ConversationViewProps {
  conversationId: string;
  onBack: () => void;
}

export function ConversationView({ conversationId, onBack }: ConversationViewProps) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const supabase = useSupabase();
  const { conversations, getConversationMessages, sendMessage, markAsRead, loadEarlierMessages, hasMore, retryMessage, refreshMessages } = useMessage();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(520);
  const [itemHeight, setItemHeight] = useState(72); // measured average height
  const buffer = 10;
  const [atBottom, setAtBottom] = useState(true);
  const [typingUsers, setTypingUsers] = useState<Record<string, number>>({});
  const [showContactInfo, setShowContactInfo] = useState(false);
  const typingTimeoutRef = useRef<number | null>(null);
  const markDebounceRef = useRef<number | null>(null);
  
  // Refs to stabilize height measurement and prevent flickering
  const measuringRef = useRef(false);
  const heightStableRef = useRef(false);
  const updateCountRef = useRef(0);
  
  // Refs to detect user vs programmatic scrolling
  const isUserScrollingRef = useRef(false);
  const autoScrollTimeoutRef = useRef<number | null>(null);
  
  // Refs for padding to avoid unnecessary re-renders
  const paddingTopRef = useRef(0);
  const paddingBottomRef = useRef(0);

  const conversation = conversations.find(c => c.id === conversationId);
  const messages = getConversationMessages(conversationId);
  
  if (DEBUG) {
    devLog.log('[ConversationView] Messages loaded:', messages.length);
  }

  useEffect(() => {
    if (conversation && conversation.unreadCount > 0) {
      markAsRead(conversationId);
    }
  }, [conversationId, conversation, markAsRead]);

  // Auto-scroll to bottom when new messages arrive (only if user was already at bottom)
  useEffect(() => {
    if (!scrollRef.current || !atBottom || isUserScrollingRef.current) {
      if (DEBUG && !atBottom) {
        devLog.log('[ConversationView] Auto-scroll skipped (not at bottom)');
      }
      if (DEBUG && isUserScrollingRef.current) {
        devLog.log('[ConversationView] Auto-scroll skipped (user is scrolling)');
      }
      return;
    }
    
    if (DEBUG) {
      devLog.log('[ConversationView] Auto-scrolling to new message');
    }
    
    // Use requestAnimationFrame to ensure layout is stable before scrolling
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, [messages.length, atBottom]);

  // Track container height
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setContainerHeight(el.clientHeight || 520);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.target as HTMLDivElement;
    setScrollTop(el.scrollTop);
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    setAtBottom(nearBottom);
    
    // Track user scrolling to prevent auto-scroll interference
    isUserScrollingRef.current = true;
    if (autoScrollTimeoutRef.current) clearTimeout(autoScrollTimeoutRef.current);
    autoScrollTimeoutRef.current = window.setTimeout(() => {
      isUserScrollingRef.current = false;
      if (DEBUG) {
        devLog.log('[ConversationView] User scrolling ended');
      }
    }, 150);
    
    if (DEBUG) {
      devLog.log('[ConversationView] Scroll position:', {
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
        atBottom: nearBottom
      });
    }
  };

  // Infinite scroll up using IntersectionObserver
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return; // fallback to button
    const node = topSentinelRef.current;
    const root = scrollRef.current || undefined;
    if (!node || !root) return;
    const obs = new IntersectionObserver(async (entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore(conversationId) && !loadingEarlier) {
        setLoadingEarlier(true);
        try {
          await loadEarlierMessages(conversationId);
        } finally {
          setLoadingEarlier(false);
        }
      }
    }, { root, threshold: 1 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [conversationId, hasMore, loadEarlierMessages, loadingEarlier]);

  // Debounced mark-as-read when at bottom and unread exist
  useEffect(() => {
    if (!conversation) return;
    if (!atBottom || conversation.unreadCount <= 0) return;
    if (markDebounceRef.current) window.clearTimeout(markDebounceRef.current);
    markDebounceRef.current = window.setTimeout(() => {
      markAsRead(conversationId);
    }, 250);
  }, [atBottom, conversation, conversationId, markAsRead]);

  // Typing indicators via ephemeral broadcast
  useEffect(() => {
    const channel = supabase.channel(`typing:${conversationId}`);
    channel.on('broadcast', { event: 'typing' }, (payload: any) => {
      const { userId, name } = payload.payload || {};
      if (!userId || userId === user?.id) return;
      setTypingUsers(prev => ({ ...prev, [name || 'Someone']: Date.now() + 2000 }));
    });
    channel.subscribe();
    const interval = window.setInterval(() => {
      const now = Date.now();
      setTypingUsers(prev => {
        const next: Record<string, number> = {};
        Object.entries(prev).forEach(([k, v]) => { if (v > now) next[k] = v; });
        return next;
      });
    }, 1000);
    return () => {
      window.clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [conversationId, user?.id]);

  const handleLoadEarlier = async () => {
    if (loadingEarlier) return;
    setLoadingEarlier(true);
    try {
      await loadEarlierMessages(conversationId);
    } finally {
      setLoadingEarlier(false);
    }
  };

  const handleSendMessage = (content: string) => {
    sendMessage(conversationId, content);
  };

  if (!conversation) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">{t('messages.conversation_not_found')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-card">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        
        <div 
          className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setShowContactInfo(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowContactInfo(true);
            }
          }}
          aria-label="View contact information"
          title="Click to view contact details"
        >
          <Avatar className="w-10 h-10 shrink-0">
            {conversation.avatarUrl && <AvatarImage src={conversation.avatarUrl} alt={conversation.name} />}
            <AvatarFallback className="bg-primary/10 text-primary">
              {conversation.avatar}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-foreground">{conversation.name}</h2>
              {conversation.type === 'group' ? (
                <Users className="h-4 w-4 text-muted-foreground" />
              ) : (
                <User className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {conversation.participants.length} participants
            </p>
          </div>
        </div>

        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setShowContactInfo(true)}
            className="shrink-0"
            title="View contact information"
          >
            <Info className="h-4 w-4" />
          </Button>
          
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={refreshMessages}
            className="shrink-0"
            title="Refresh messages"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages with simple windowing */}
      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto p-4 space-y-4">
        <div ref={topSentinelRef} />
        {hasMore(conversationId) && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={handleLoadEarlier} disabled={loadingEarlier}>
              {loadingEarlier ? t('loading') : t('messages.load_earlier')}
            </Button>
          </div>
        )}
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{t('messages.no_messages_yet')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, idx) => {
              const prevMsg = idx > 0 ? messages[idx - 1] : null;
              const prevDay = prevMsg ? prevMsg.timestamp.toDateString() : '';
              const thisDay = message.timestamp.toDateString();
              const showDateHeader = thisDay !== prevDay;
              const lastReadAt = conversation.lastReadAt;
              const showNewDivider = lastReadAt && message.timestamp > lastReadAt && 
                (!prevMsg || prevMsg.timestamp <= lastReadAt);
              
              return (
                <React.Fragment key={message.id}>
                  {showDateHeader && (
                    <div className="flex justify-center my-2">
                      <div className="text-xs text-muted-foreground bg-background px-2 py-1 rounded">
                        {thisDay}
                      </div>
                    </div>
                  )}
                  {showNewDivider && (
                    <div className="flex justify-center my-2">
                      <div className="text-xs text-primary font-medium">
                        {t('messages.new_messages')}
                      </div>
                    </div>
                  )}
                  <div
                    className={`flex gap-2 ${message.senderId === user?.id ? 'justify-end' : 'justify-start'}`}
                    data-message-row="1"
                  >
                    {message.senderId !== user?.id && (
                      <Avatar className="w-8 h-8 shrink-0 mt-1">
                        {message.senderAvatarUrl && <AvatarImage src={message.senderAvatarUrl} alt={message.senderName} />}
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {message.senderName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className={`max-w-[70%]`}>
                      <Card className={`${
                        message.senderId === user?.id 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-card'
                      }`}>
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">
                              {message.senderId === user?.id ? t('messages.you') : message.senderName}
                            </span>
                            {message.isAdmin && (
                              <Badge variant="secondary" className="text-xs">{t('members.admin')}</Badge>
                            )}
                            <span className="text-xs opacity-70">{format(message.timestamp, 'HH:mm')}</span>
                            {message.pending && (
                              <span className="text-xs opacity-70">{t('loading')}</span>
                            )}
                            {message.failed && (
                              <Button
                                size="sm"
                                variant="destructive"
                                aria-label={t('messages.retry')}
                                onClick={() => retryMessage(conversationId, message.id)}
                              >
                                {t('messages.retry')}
                              </Button>
                            )}
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </CardContent>
                      </Card>
                    </div>
                    {message.senderId === user?.id && (
                      <Avatar className="w-8 h-8 shrink-0 mt-1">
                        {message.senderAvatarUrl && <AvatarImage src={message.senderAvatarUrl} alt={message.senderName} />}
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {message.senderName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
            {/* Stable scroll anchor placed after all messages */}
            <div ref={messagesEndRef} style={{ height: '1px' }} />
          </div>
        )}
      </div>

      {!atBottom && (
        <div className="fixed bottom-24 right-4 z-50">
          <Button 
            aria-label={t('messages.scroll_to_latest')} 
            size="sm" 
            className="h-8 px-3 py-1 text-xs shadow-lg" 
            onClick={() => {
              if (scrollRef.current) {
                // Scroll to absolute bottom using scrollHeight for reliability
                requestAnimationFrame(() => {
                  if (scrollRef.current) {
                    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                    setAtBottom(true);
                  }
                });
              }
            }}
          >
            {t('messages.scroll_to_latest')}
          </Button>
        </div>
      )}

      {/* Message Input */}
      <div className="border-t bg-card p-4">
        <MessageInput onSendMessage={handleSendMessage} onTyping={() => {
          if (typingTimeoutRef.current) return;
          typingTimeoutRef.current = window.setTimeout(() => { typingTimeoutRef.current = null; }, 1000);
          supabase.channel(`typing:${conversationId}`).send({ type: 'broadcast', event: 'typing', payload: { userId: user?.id, name: user?.firstName } });
        }} />
        {!!Object.keys(typingUsers).length && (
          <div className="text-xs text-muted-foreground mt-1">{Object.keys(typingUsers).join(', ')} {t('messages.typing')}</div>
        )}
      </div>

      <ContactInfoSheet 
        conversationId={conversationId}
        open={showContactInfo}
        onOpenChange={setShowContactInfo}
      />
    </div>
  );
}
